// Styles Explorer — the full-library browser behind the sidebar's
// "Browse all styles…" button (concept §2). Portaled to document.body like
// TextureExplorerModal, ~1100×640.
//
// LIVE APPLY + OPEN-SNAPSHOT REVERT (concept decision, not option):
//   - Clicking a card applies immediately via editor.applyTextStyle (the one
//     self-batching engine method — one undo entry per click); the modal
//     stays open so the teacher keeps trying looks.
//   - On open we snapshot the selected object's full styleable state; footer
//     Revert replays it through the NON-self-saving engine setters inside a
//     single apply(async…, 'now') so exactly ONE history commit lands.
//
// TRUE previews at library scale: every thumb is stylePreviewRenderer output
// (the same composeRecipePatch the engine applies — thumbs cannot drift from
// the canvas), rendered on the user's own words/font/color. With ~270
// visible styles the grid renders LAZILY: IntersectionObserver marks cards
// visible, an idle
// queue (requestIdleCallback, setTimeout(16) fallback) feeds the renderer one
// thumb per slice so scrolling never jams. Placeholder = dark tile + label.
//
// The right "Customize" rail is the shared TextCustomizePanel — the entire
// sidebar tweak surface inside the modal, driven by its own useSelectionProps
// instance. FillSection's "Browse all textures…" opens the Texture Explorer
// OVER this modal (higher z-index + the Esc stack below).
import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelectionProps } from '../../hooks/useSelectionProps';
import { renderStylePreview } from './stylePreviewRenderer';
import styleManifest from './styles/styleManifest.json';
import TextCustomizePanel from './TextCustomizePanel';

// The 3D tab's surface. LAZY by law: Text3DPanel statically imports three,
// and three must never load at editor boot — the import() only fires the
// first time the 3D tab actually renders.
const Text3DPanel = lazy(() => import('./Text3DPanel.jsx'));
// The Bevel & Emboss tab's surface. Pure 2D (no three), but still lazy so the
// emboss recipe/preview chunk only loads when the teacher opens the tab.
const EmbossPanel = lazy(() => import('./EmbossPanel.jsx'));

// ---------------------------------------------------------------- Esc stack
// Shared capture-phase Escape stack for the editor's stacked modals. Each
// modal pushes a handler on mount and pops it on unmount; Esc fires ONLY the
// top of the stack (texture explorer over styles modal closes first; the
// next Esc closes the styles modal). One module-level document listener,
// capture phase, attached only while at least one modal is open — it
// swallows the event before the editor's deselect hotkey can see it.
// TextureExplorerModal imports these (runtime-only calls, so the module
// cycle styles-modal → customize-panel → fill-section → texture-modal is
// harmless).
const escStack = [];

function onEscCapture(e) {
  if (e.key !== 'Escape' || escStack.length === 0) return;
  e.stopPropagation();
  e.preventDefault();
  escStack[escStack.length - 1]?.();
}

export function pushEscHandler(handler) {
  if (escStack.length === 0) document.addEventListener('keydown', onEscCapture, true);
  escStack.push(handler);
}

export function popEscHandler(handler) {
  const i = escStack.lastIndexOf(handler);
  if (i !== -1) escStack.splice(i, 1);
  if (escStack.length === 0) document.removeEventListener('keydown', onEscCapture, true);
}

// ------------------------------------------------------------------- consts
const PREVIEW_MAX_CHARS = 16;
const FALLBACK_SAMPLE = 'Aa Bb 123';
const THUMB_W = 200; // card ≈200×96: thumb render + label row
const THUMB_H = 64;
const STRIP_W = 640;
const STRIP_H = 110;

const STYLES = styleManifest.styles;
const CATEGORIES = styleManifest.categories;
// Curated-out legacy styles keep their manifest entry (stable ids for old
// designs) but carry hidden:true — they never surface in the browse grid or
// search. Curation guarantees featured ∩ hidden = ∅, so the sidebar chips
// can't hit them either. STYLE_BY_ID keeps ALL ids: hover/click/strip only
// ever see ids from rendered (visible) cards, and old designs still resolve.
const VISIBLE_STYLES = STYLES.filter((s) => !s.hidden);
const STYLE_BY_ID = new Map(STYLES.map((s) => [s.id, s]));

/** First line of the selected object's text, truncated for previews. */
function previewSample(obj) {
  const first = String(obj?.text ?? '').split('\n')[0].trim();
  if (!first) return FALLBACK_SAMPLE;
  return first.length > PREVIEW_MAX_CHARS ? `${first.slice(0, PREVIEW_MAX_CHARS)}…` : first;
}

/** requestIdleCallback with a setTimeout(16) fallback — thumb renders ride
 * idle slices so grid scroll stays smooth on weak classroom laptops. */
const scheduleIdle = (cb) => (
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback(cb, { timeout: 200 })
    : setTimeout(cb, 16)
);

/** Open-snapshot for Revert: everything a style click or a rail tweak can
 * touch, read from the LIVE object (data bag first, live-derived fallbacks —
 * same derivations useSelectionProps mirrors). textEffects is deep-copied:
 * the engine mutates data.textEffects entries by reference. */
function takeRevertSnapshot(obj) {
  if (!obj) return null;
  const fx = obj.data?.textEffects;
  return {
    textFill: obj.data?.textFill
      ?? (obj.fill?.colorStops?.length
        ? {
          type: obj.fill.type === 'radial' ? 'radial-gradient' : 'linear-gradient',
          stops: obj.fill.colorStops.map((s) => ({ offset: s.offset, color: s.color })),
        }
        : { type: 'solid', color: typeof obj.fill === 'string' && obj.fill !== 'transparent' ? obj.fill : '#000000' }),
    textEffects: fx ? JSON.parse(JSON.stringify(fx)) : null,
    highlight: obj.data?.highlight
      ?? (obj.textBackgroundColor ? { color: obj.textBackgroundColor, fade: 0 } : null),
    textBackgroundColor: obj.textBackgroundColor || null,
    charSpacing: obj.charSpacing ?? 0,
    lineHeight: obj.lineHeight ?? 1.16,
    letterCase: obj.data?.letterCase ?? 'none',
    blendMode: obj.globalCompositeOperation ?? 'source-over',
    opacity: obj.opacity ?? 1,
    curveAmount: obj.curveAmount ?? 0,
  };
}

// ------------------------------------------------------------------- modal
export default function StylesExplorerModal({ editor, selectedObjects, onClose, initialCategory }) {
  // Own mirror/commit instance — multi-instance safe by design. The modal
  // only needs apply(); the customize rail runs its own instance too.
  const { apply } = useSelectionProps(editor, selectedObjects);

  // The sidebar's depth shortcuts deep-link straight to a rail category:
  // Bevel & Emboss / 3D are the LAST rail entries and nothing in the sidebar
  // hinted they existed, so they were effectively undiscoverable.
  const [category, setCategory] = useState(initialCategory || 'all');
  const [query, setQuery] = useState('');
  const [ground, setGround] = useState('light'); // preview backdrop tone
  const [hoverId, setHoverId] = useState(null);
  // Applied style is tracked LOCALLY (last clicked card) — the objProps
  // mirror has no appliedStyleId field, and deriving one on open is not
  // possible; starting with none is correct.
  const [appliedId, setAppliedId] = useState(null);
  const [tweaksOpen, setTweaksOpen] = useState(false);
  const [dirty, setDirty] = useState(false); // anything changed since open?
  const [thumbs, setThumbs] = useState({}); // `${styleId}|${ground}` → blob URL
  const [stripUrl, setStripUrl] = useState(null);

  // Esc closes only the top-most modal (see stack above).
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const handler = () => closeRef.current?.();
    pushEscHandler(handler);
    return () => popEscHandler(handler);
  }, []);

  // Sanctioned read-only peek (texture-modal precedent): the user's words +
  // font + text color drive every preview. Frozen at open so the ~270 cache
  // keys stay stable even if a rail tweak (letter case, a color pick, a card
  // click) rewrites obj.text/obj.fill mid-session.
  const sampleRef = useRef(null);
  if (sampleRef.current === null) {
    const sel = editor?.selectedObjects?.[0];
    sampleRef.current = {
      text: previewSample(sel),
      fontFamily: sel?.fontFamily,
      fontWeight: sel?.fontWeight,
      // The teacher's real text color (same derivation objProps.fill
      // mirrors) — kept-fill recipes (Shadow Pop…) preview on it instead of
      // the ground default. Part of the renderer's cache key.
      baseFill: typeof sel?.fill === 'string' && sel.fill !== 'transparent' ? sel.fill : '#000000',
    };
  }
  const sample = sampleRef.current;

  // 3D seed — the teacher's actual words (first line, untruncated, up to the
  // panel's 60-char cap) + their font, captured once at open so a placed 3D
  // headline says what they had selected, not a placeholder.
  const seed3dRef = useRef(null);
  if (seed3dRef.current === null) {
    const sel = editor?.selectedObjects?.[0];
    const line = String(sel?.text ?? '').split('\n')[0].trim();
    seed3dRef.current = {
      text: line.slice(0, 60),
      fontFamily: sel?.fontFamily,
      // The live object itself: insert REPLACES it ("apply to my text"), so
      // the teacher's selection transforms instead of gaining a stacked twin.
      // Only live text qualifies — a selected baked picture stays put.
      sourceObj: sel && typeof sel.text === 'string' ? sel : null,
    };
  }

  // Panel tabs (3D / Bevel) register their submit here; the modal footer's
  // primary button drives it. Success closes via the panel's onInserted —
  // on failure the modal stays open with the panel's error visible.
  const panelSubmitRef = useRef(null);
  const [panelBusy, setPanelBusy] = useState(false);
  const registerPanelSubmit = useCallback((fn) => { panelSubmitRef.current = fn; }, []);
  const handlePanelAdd = async () => {
    const fn = panelSubmitRef.current;
    if (!fn) return; // panel chunk still loading — nothing to submit yet
    setPanelBusy(true);
    try { await fn(); } finally { setPanelBusy(false); }
  };

  // Revert snapshot — taken once, at open.
  const snapshotRef = useRef(null);
  if (snapshotRef.current === null) {
    snapshotRef.current = takeRevertSnapshot(editor?.selectedObjects?.[0]);
  }

  // A search term matches label AND tags, across ALL categories, overriding
  // the rail selection. The Featured rail entry filters on the flag (no
  // style carries category:'featured').
  const q = query.trim().toLowerCase();
  // The 3D tab replaces the preview strip + grid with the live Text3DPanel.
  // A search term still overrides the rail (same rule as every category), so
  // typing in the search box while on 3D shows the matching 2D styles.
  const is3d = !q && category === '3d';
  // The Bevel & Emboss tab does the same with EmbossPanel. Both "panel tabs"
  // hide the 2D-only chrome (Customize rail, Revert) since they never touch
  // the selected text — they place a fresh baked picture.
  const isBevel = !q && category === 'bevel';
  const isPanelTab = is3d || isBevel;
  const shown = useMemo(() => {
    if (q) {
      return VISIBLE_STYLES.filter((s) => s.label.toLowerCase().includes(q)
        || (s.tags || []).some((t) => t.toLowerCase().includes(q)));
    }
    if (category === 'all') return VISIBLE_STYLES;
    if (category === 'featured') return VISIBLE_STYLES.filter((s) => s.featured);
    return VISIBLE_STYLES.filter((s) => s.category === category);
  }, [q, category]);

  // ------------------------------------------------ lazy thumb machinery
  const groundRef = useRef(ground);
  groundRef.current = ground;
  const thumbsRef = useRef(thumbs);
  thumbsRef.current = thumbs;
  const queuedRef = useRef(new Set()); // keys queued or in flight
  const queueRef = useRef([]);
  const pumpingRef = useRef(false);
  const aliveRef = useRef(true);
  useEffect(() => {
    // Re-arm on mount: StrictMode's simulated unmount runs the cleanup but
    // keeps the refs, so a set-only cleanup would kill the pump forever.
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Thumbs render through idle slices; while the slice's deadline still has
  // room we chain the next render in the same slice (a thumb is ~2-3 ms),
  // otherwise we yield and reschedule — scroll never jams. renderStylePreview
  // serializes internally and LRU-caches, so re-queues after a ground flip
  // are near-free.
  const pump = useCallback(() => {
    if (pumpingRef.current) return;
    pumpingRef.current = true;
    const step = (deadline) => {
      if (!aliveRef.current) { pumpingRef.current = false; return; }
      const job = queueRef.current.shift();
      if (!job) { pumpingRef.current = false; return; }
      const s = STYLE_BY_ID.get(job.id);
      const { text, fontFamily, fontWeight, baseFill } = sampleRef.current;
      renderStylePreview({
        recipe: s.recipe,
        text,
        fontFamily,
        fontWeight,
        ground: job.g,
        width: THUMB_W,
        height: THUMB_H,
        cacheKey: `${s.id}|${job.g}`,
        baseFill, // keyFor appends it — no cacheKey change needed
      }).then((url) => {
        if (aliveRef.current) setThumbs((m) => ({ ...m, [job.key]: url }));
      }).catch(() => {
        queuedRef.current.delete(job.key); // allow a retry on re-scroll
      }).finally(() => {
        if (deadline && typeof deadline.timeRemaining === 'function' && deadline.timeRemaining() > 4) {
          step(deadline); // same slice, room left
        } else {
          scheduleIdle(step);
        }
      });
    };
    scheduleIdle(step);
  }, []);

  // groundHint 'dark' pins that style's thumb to the dark ground regardless
  // of the toggle (neon/glow recipes are illegible on light).
  const enqueueThumb = useCallback((id) => {
    const s = STYLE_BY_ID.get(id);
    if (!s) return;
    const g = s.groundHint === 'dark' ? 'dark' : groundRef.current;
    const key = `${id}|${g}`;
    if (thumbsRef.current[key] || queuedRef.current.has(key)) return;
    queuedRef.current.add(key);
    queueRef.current.push({ id, key, g });
    pump();
  }, [pump]);

  // IntersectionObserver on the cards, grid as root. Cards register via ref
  // callback into nodesRef; the observer picks up pre-registered nodes on
  // creation and new ones as they mount (category/search swaps).
  const gridRef = useRef(null);
  const ioRef = useRef(null);
  const nodesRef = useRef(new Map()); // styleId → element
  const visibleRef = useRef(new Set()); // styleIds currently near-viewport

  const registerCard = useCallback((node) => {
    if (!node) return; // unmount: observer holds a dead node briefly — harmless
    const id = node.getAttribute('data-style-id');
    nodesRef.current.set(id, node);
    if (ioRef.current) ioRef.current.observe(node);
    else if (typeof IntersectionObserver === 'undefined') enqueueThumb(id);
  }, [enqueueThumb]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      for (const en of entries) {
        const id = en.target.getAttribute('data-style-id');
        if (!id) continue;
        if (en.isIntersecting) {
          visibleRef.current.add(id);
          enqueueThumb(id);
        } else {
          visibleRef.current.delete(id);
        }
      }
    }, { root: gridRef.current, rootMargin: '160px 0px' });
    ioRef.current = io;
    nodesRef.current.forEach((node) => io.observe(node));
    return () => {
      io.disconnect();
      ioRef.current = null;
    };
  }, [enqueueThumb]);

  // Ground flip: visible cards re-queue for the new ground (cache-warm ones
  // resolve instantly; blob URLs stay owned by the renderer's LRU).
  useEffect(() => {
    visibleRef.current.forEach((id) => enqueueThumb(id));
  }, [ground, enqueueThumb]);

  // ------------------------------------------------------- preview strip
  // True render of the hovered style, else the applied one, at strip size.
  const stripStyleId = hoverId ?? appliedId;
  useEffect(() => {
    if (!stripStyleId || !STYLE_BY_ID.has(stripStyleId)) {
      setStripUrl(null);
      return undefined;
    }
    const s = STYLE_BY_ID.get(stripStyleId);
    let stale = false;
    renderStylePreview({
      recipe: s.recipe,
      text: sample.text,
      fontFamily: sample.fontFamily,
      fontWeight: sample.fontWeight,
      ground,
      width: STRIP_W,
      height: STRIP_H,
      cacheKey: `strip|${s.id}`,
      baseFill: sample.baseFill,
    }).then((url) => { if (!stale) setStripUrl(url); })
      .catch(() => { if (!stale) setStripUrl(null); });
    return () => { stale = true; };
  }, [stripStyleId, ground, sample]);

  const plainStripStyle = {
    fontFamily: sample.fontFamily || 'Lexend, sans-serif',
    fontWeight: sample.fontWeight || 700,
    color: ground === 'light' ? '#0f172a' : '#f8fafc',
  };

  // --------------------------------------------------------- interactions
  // Delegation: cards carry data-style-id; the grid owns the only handlers.
  const styleIdFrom = (e) => e.target.closest?.('[data-style-id]')?.getAttribute('data-style-id') ?? null;

  const handleGridClick = (e) => {
    const id = styleIdFrom(e);
    if (!id) return;
    const s = STYLE_BY_ID.get(id);
    if (!s) return;
    setAppliedId(id);
    setDirty(true);
    // LIVE apply; applyTextStyle self-batches into ONE undo entry, and the
    // trailing commitNow dedupes to a no-op (same path as the sidebar chips).
    apply(() => editor?.applyTextStyle({ id: s.id, label: s.label, recipe: s.recipe }), 'now');
  };

  const handleGridHover = (e) => {
    const id = styleIdFrom(e);
    if (id) setHoverId(id);
  };

  // Revert = replay the open snapshot through the NON-self-saving setters,
  // all inside ONE apply(async…, 'now') → apply awaits the promise, then
  // commits once → a single history entry. Order matters: effects first
  // (hollow's fill routing must be in place before setTextFill decides
  // whether the paint lands on fill or stroke).
  const handleRevert = () => {
    const snap = snapshotRef.current;
    if (!snap || !editor) return;
    setAppliedId(null);
    setHoverId(null);
    setDirty(false);
    apply(async () => {
      await editor.clearTextEffects();
      if (snap.textEffects) {
        const seen = new Set(); // sticker spans both slots → ONE applyTextEffect
        for (const slot of ['shadow', 'stroke']) {
          const entry = snap.textEffects[slot];
          if (entry?.id && !seen.has(entry.id)) {
            seen.add(entry.id);
            await editor.applyTextEffect(entry.id, entry.params);
          }
        }
      }
      await editor.setTextFill(snap.textFill);
      editor.setTextHighlight(snap.highlight);
      editor.changeCharSpacing(snap.charSpacing);
      editor.changeLineHeight(snap.lineHeight);
      editor.setLetterCase(snap.letterCase);
      editor.changeBlendMode(snap.blendMode);
      editor.changeOpacity(snap.opacity);
      editor.changeTextCurve(snap.curveAmount);
    }, 'now');
  };

  const appliedStyle = appliedId ? STYLE_BY_ID.get(appliedId) : null;

  return createPortal(
    <div
      className="cce-stylemodal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="cce-stylemodal" role="dialog" aria-modal="true" aria-label="Text styles">
        <div className="cce-stylemodal-head">
          <h2 className="cce-stylemodal-title">Text styles</h2>
          <input
            type="search"
            className="cce-stylemodal-search"
            placeholder="Search all styles…"
            aria-label="Search text styles"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setHoverId(null); }}
          />
          {/* Customize tweaks 2D text-style props — meaningless on a panel
              tab (3D / Bevel), where every knob lives inside the panel. */}
          {!isPanelTab && (
            <button
              type="button"
              className={`cce-stylemodal-customize-toggle${tweaksOpen ? ' active' : ''}`}
              aria-expanded={tweaksOpen}
              onClick={() => setTweaksOpen((v) => !v)}
            >
              {tweaksOpen ? 'Customize ▾' : 'Customize ▸'}
            </button>
          )}
          <button type="button" className="cce-stylemodal-close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="cce-stylemodal-main">
          <div className="cce-stylemodal-rail">
            {[{ id: 'all', label: 'All' }, ...CATEGORIES].map((c) => (
              <button
                key={c.id}
                type="button"
                data-cat={c.id}
                className={`cce-stylemodal-cat${!q && category === c.id ? ' active' : ''}`}
                onClick={() => { setCategory(c.id); setQuery(''); setHoverId(null); }}
              >
                {c.label}
              </button>
            ))}
            {/* Real 3D text — swaps the content area for the live panel. */}
            <button
              type="button"
              data-cat="3d"
              className={`cce-stylemodal-cat cce-stylemodal-cat-3d${is3d ? ' active' : ''}`}
              title="Real 3D text"
              onClick={() => { setCategory('3d'); setQuery(''); setHoverId(null); }}
            >
              3D
            </button>
            {/* Bevel & Emboss — raised / carved letters (2D bake). */}
            <button
              type="button"
              data-cat="bevel"
              className={`cce-stylemodal-cat cce-stylemodal-cat-bevel${isBevel ? ' active' : ''}`}
              title="Raised & carved letters"
              onClick={() => { setCategory('bevel'); setQuery(''); setHoverId(null); }}
            >
              Bevel &amp; Emboss
            </button>
          </div>

          <div className="cce-stylemodal-content">
            {is3d ? (
              // Live 3D surface in place of the strip + grid. Suspense guards
              // the lazy chunk (three + panel) on first open; onInserted
              // closes the WHOLE modal — the teacher lands on their freshly
              // placed (and selected) 3D text.
              <div className="cce-stylemodal-3dwrap">
                <Suspense fallback={<div className="cce-stylemodal-3dloading">Loading 3D…</div>}>
                  <Text3DPanel
                    editor={editor}
                    onInserted={onClose}
                    seedText={seed3dRef.current.text}
                    seedFont={seed3dRef.current.fontFamily}
                    seedSource={seed3dRef.current.sourceObj}
                    registerSubmit={registerPanelSubmit}
                  />
                </Suspense>
              </div>
            ) : isBevel ? (
              // Live Bevel & Emboss surface — same seat as the 3D panel, seeded
              // from the same selection snapshot; onInserted closes the modal.
              <div className="cce-stylemodal-3dwrap cce-stylemodal-bevelwrap">
                <Suspense fallback={<div className="cce-stylemodal-3dloading">Loading…</div>}>
                  <EmbossPanel
                    editor={editor}
                    onInserted={onClose}
                    seedText={seed3dRef.current.text}
                    seedFont={seed3dRef.current.fontFamily}
                    seedSource={seed3dRef.current.sourceObj}
                    registerSubmit={registerPanelSubmit}
                  />
                </Suspense>
              </div>
            ) : (
              <>
            <div className="cce-stylemodal-preview-wrap">
              <div className={`cce-stylemodal-preview ${ground}`}>
                {stripStyleId && stripUrl ? (
                  <img
                    className="cce-stylemodal-preview-img"
                    src={stripUrl}
                    alt=""
                    draggable={false}
                  />
                ) : (
                  <span className="cce-stylemodal-preview-text" style={plainStripStyle}>
                    {sample.text}
                  </span>
                )}
                <div className="cce-stylemodal-grounds" role="group" aria-label="Preview background">
                  <button
                    type="button"
                    className={`cce-stylemodal-ground light${ground === 'light' ? ' active' : ''}`}
                    title="Light background"
                    aria-label="Preview on a light background"
                    onClick={() => setGround('light')}
                  />
                  <button
                    type="button"
                    className={`cce-stylemodal-ground dark${ground === 'dark' ? ' active' : ''}`}
                    title="Dark background"
                    aria-label="Preview on a dark background"
                    onClick={() => setGround('dark')}
                  />
                </div>
              </div>
            </div>

            <div
              className="cce-stylemodal-grid"
              ref={gridRef}
              onClick={handleGridClick}
              onMouseOver={handleGridHover}
              onFocus={handleGridHover}
              onMouseLeave={() => setHoverId(null)}
            >
              {shown.map((s) => {
                const g = s.groundHint === 'dark' ? 'dark' : ground;
                const url = thumbs[`${s.id}|${g}`];
                return (
                  <button
                    key={s.id}
                    type="button"
                    data-style-id={s.id}
                    className={`cce-stylemodal-card${appliedId === s.id ? ' active' : ''}`}
                    title={s.label}
                    aria-label={`Text style ${s.label}`}
                    ref={registerCard}
                  >
                    <span className={`cce-stylemodal-card-thumb ${g}`}>
                      {url
                        ? <img src={url} alt="" draggable={false} />
                        : <span className="cce-stylemodal-card-ph">{s.label}</span>}
                      {s.groundHint === 'dark' && (
                        <span
                          className="cce-stylemodal-darkdot"
                          title="Previewed on a dark background"
                          aria-hidden="true"
                        />
                      )}
                    </span>
                    <span className="cce-stylemodal-card-label">{s.label}</span>
                  </button>
                );
              })}
              {shown.length === 0 && (
                <div className="cce-stylemodal-empty">No styles match “{query.trim()}”.</div>
              )}
            </div>
              </>
            )}
          </div>

          {tweaksOpen && !isPanelTab && (
            <div className="cce-stylemodal-tweaks">
              <div className="cce-inspector-content">
                <TextCustomizePanel
                  editor={editor}
                  selectedObjects={selectedObjects}
                  dense
                  onMutate={() => setDirty(true)}
                />
              </div>
            </div>
          )}
        </div>

        <div className="cce-stylemodal-footer">
          {/* On a panel tab (3D / Bevel) the footer's primary button IS the
              submit — a primary "Done" that merely closed threw the teacher's
              work away (owner report, 2026-07-15). Cancel is the quiet exit;
              Esc and × still work. */}
          <span className="cce-stylemodal-current">
            {is3d
              ? '3D text lands on your poster as a picture — double-click it any time to edit.'
              : isBevel
                ? 'Bevel text lands on your poster as a picture — double-click it any time to edit.'
                : appliedStyle
                  ? <>Current style: <strong>{appliedStyle.label}</strong></>
                  : 'No style picked yet — click a card to try one.'}
          </span>
          <div className="cce-stylemodal-actions">
            {isPanelTab ? (
              <>
                <button
                  type="button"
                  className="cce-inspector-mini-btn cce-stylemodal-cancel"
                  disabled={panelBusy}
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="cce-inspector-mini-btn primary cce-stylemodal-add"
                  disabled={panelBusy}
                  onClick={handlePanelAdd}
                >
                  {panelBusy ? 'Working…' : seed3dRef.current.sourceObj ? 'Apply to my text' : 'Add to poster'}
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="cce-inspector-mini-btn cce-stylemodal-revert"
                  disabled={!dirty}
                  onClick={handleRevert}
                >
                  Revert
                </button>
                <button
                  type="button"
                  className="cce-inspector-mini-btn primary cce-stylemodal-done"
                  onClick={onClose}
                >
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
